import {html, nothing} from "lit";
import {MobxLitElement} from "@adobe/lit-mobx";
import {customElement, query, state} from "lit/decorators.js";
import "./common/controls-inputs/Button.js";
import "./common/controls-inputs/LabeledTextInput.js";
import "./common/controls-inputs/Select.js";
import Data from "../main/data/index.js";
import Features from "../main/utils/features.js";
import {citationFormats} from "../main/citation.js";
import Project from "../main/project.js";
import {attachModal, Modal, MODALS} from "./common/modals.js";
import {modalStyles} from "./common/styles/Modals.js";

@customElement("pn-create-project-modal")
export class CreateProjectModal extends MobxLitElement {
	@state() name = "";
	@state() description:    string | null = null;
	@state() citationFormat: string;
	// @state() citationFormat: string = ""; // TODO: add to Data.project.create so we can use this
	@state() error:          string | boolean = false;
	@state() fieldErrors = {
		name: false
	};

	@query(".modal-content") modalContent: HTMLElement;

	static styles = [modalStyles];

	modal: Modal | null = null;

	constructor() {
		super();

		this.citationFormat = Project.getCitationStyle(Data.app.project);
	}

	firstUpdated() {
		this.modal = new Modal({
			id:               "create-project",
			modalContent:     this.modalContent,
			parent:           this.shadowRoot,
			isSingleUseModal: true
		});
		this.modal.show();
	}

	validateForm() {
		this.fieldErrors = {
			name: this.name.trim().length === 0
		};

		return !!this.fieldErrors.name;
	}

	onClickCreate = async () => {
		const hasErrors = this.validateForm();
		if (hasErrors)
			return;

		try {
			const newProject = await Data.project.create({
				name:        this.name.trim(),
				description: this.description?.trim(),
				settings:    {
					citations_preferred_format: this.citationFormat
				}
			});
			Data.app.setCurrentProject(newProject.uuid);
			this.error = false;
			this.modal?.hide();
		} catch (e) {
			console.error(e);
			this.error = e.message;
		}
	};

	keydownOnForm = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			this.onClickCreate();
		}
	};

	renderForm() {
		const citationFormatOptions = citationFormats;
		const citationFormatSelected = citationFormatOptions.find(option =>
			option.value === this.citationFormat
		);

		return html`
			<div class="heading"><h2>Create a new project</h2></div>
			
			<div class="body">
				<form>
					<pn-labeled-text-input
						.error=${this.fieldErrors.name}
						id="pn-name"
						inputType="text"
						label="Project name"
						.required=${true}
						.onInput=${(e: {target: {value: string}}) => {
							this.name = e.target.value.trim();
						}}
						.onKeydown=${this.keydownOnForm}
						.value=${this.name}
					></pn-labeled-text-input>

					<label>
						Project description
						<textarea
							id="create-project-modal-description"
							@input=${(e: {target: {value: string}}) => {
								this.description = e.target.value.trim() || null;
							}}
							.value=${this.description}
						></textarea>
					</label>

					<label>
						Citation format
						<pn-select
							id="create-project-modal-citation-format"
							.options=${citationFormatOptions}
							.selectedOption=${citationFormatSelected}
							.onSelectCallback=${(format: string) => {this.citationFormat = format;}}
						/>
					</label>
				</form>
			</div>

			<div class="buttons multiple-btn">
				<pn-button
					.onClick=${() => this.modal?.hide()}
					renderType="secondary"
				>Cancel</pn-button>

				<pn-button
					.onClick=${this.onClickCreate}
					renderType="primary"
				>Create</pn-button>
			</div>

			${this.error ? html`<div id="modal-error">
				<p class="modal-error-msg">${this.error === true ? "Unknown error" : this.error}</p>
			</div>` : nothing}
		`;
	}

	renderLimitReached() {
		let body;
		const projectLimit = Data.app.me?.available_projects;
		switch (Data.app.me?.integration) {
			case "proquest": {
				const options = Data.app.me?.email
					? html`
						<li><a
							href=${Data.app.getWebAppAuthLink("project")}
							rel="noreferrer"
							target="_blank"
						>Manage Projects</a> to edit or delete your Congressional PowerNotes projects.</li>
						<li><a
							href=${Data.app.getWebAppAuthLink("account#upgrade")}
							rel="noreferrer"
							target="_blank"
						>Upgrade</a> your PowerNotes account to get extra projects.</li>
					`
					: html`
						<li><pn-button
							.onClick=${() => attachModal(MODALS.INT_SIGNUP)}
							renderType="link-blue"
						>Upgrade</pn-button> your PowerNotes account to get extra projects.</li>
					`;

				body = html`
					<p>
						You have created <strong>${projectLimit}</strong> of the <strong>${projectLimit}</strong>
						projects you can create with PowerNotes in Congressional.
					</p>
					<p>
						You can:
					</p>
					<ul>
						<li>Continue adding to and working with your existing projects in the sidebar and highlight tool.</li>
						${options}
					</ul>
				`;
				break;
			}
			default: {
				const options = Data.app.me?.email
					? html`
						<li><a
							href=${Data.app.getWebAppAuthLink("project")}
							rel="noreferrer"
							target="_blank"
						>Manage Projects</a> to edit or delete your PowerNotes projects.</li>
						<li><a
							href=${Data.app.getWebAppAuthLink("account#upgrade")}
							rel="noreferrer"
							target="_blank"
						>Upgrade</a> your PowerNotes account to get extra projects.</li>
					`
					: html`
						<li><pn-button
							.onClick=${() => attachModal(MODALS.INT_SIGNUP)}
							renderType="link-blue"
						>Upgrade</pn-button> your PowerNotes account to get extra projects.</li>
					`;

				body = html`
					<p>
						You have created <strong>${projectLimit}</strong> of the <strong>${projectLimit}</strong>
						projects you can create with PowerNotes.
					</p>
					<p>
						You can:
					</p>
					<ul>
						<li>Continue adding to and working with your existing projects in the sidebar and highlight tool.</li>
						${options}
					</ul>
				`;
			}
		}

		return html`
			<div class="heading"><h2>Maximum projects reached</h2></div>

			<div class="body">
				${body}
			</div>

			<div class="buttons multiple-btn">
				<pn-button
					.onClick=${() => this.modal?.hide()}
					renderType="secondary"
				>Close</pn-button>
			</div>
		`;
	}

	render() {
		const canCreate = Features.hasMoreAvailableProjects(Data.app.me, Data.project.all);

		return html`
			<div class="modal-content">
				<div id="pn-modal-content">
					${canCreate ? this.renderForm() : this.renderLimitReached()}
				</div>
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-create-project-modal": CreateProjectModal;
	}
}
